import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import styles from './icons.module.css';

/**
 * Общая обвязка значков Мезы.
 *
 * Значки рисуются ТОНКОЙ линией в currentColor и не имеют заливки: так они
 * набраны в макетах и так же они берут цвет от кнопки или подписи, рядом с
 * которой стоят. Вкуишные иконки сюда не годятся — они сплошные, 24 px и в
 * своей палитре: рядом с контурной кнопкой они читаются как чужой элемент.
 *
 * Поле 14×14 по умолчанию, чтобы значки не прыгали по высоте в одном ряду;
 * значкам разделов оно тесно, и они просят 20×20 явно. Коробка бывает и
 * НЕквадратной — у шеврона она узкая и высокая, как в макетах, — тогда её
 * задают парой.
 *
 * Концы и стыки СКРУГЛЁННЫЕ — у всего набора разом. На тонкой линии в один
 * пиксел это почти не видно на прямых, зато снимает острые заусенцы на стыках
 * и даёт правильный вид фигурам, нарисованным дугами.
 *
 * `strokeWidth` переопределяют по одной причине: фигура нарисована в своей
 * системе координат, и при выводе в другом размере линия масштабируется вместе
 * с ней. У шеврона 1.1 в коробке 14, выведенной на 12 px, давало 0.94 px — он
 * выглядел непрокрашенным рядом с макетными 1.4.
 *
 * `label` нужен там, где значок стоит ОДИН и сам является сообщением (значок
 * ссылки на плитке внешки). Рядом с подписью он остаётся скрытым от
 * скринридера: иначе тот прочитает «править править».
 */
export type IconTone = 'accent' | 'muted' | 'pink' | 'lilac' | 'violet';

export function Icon({
  size = 13,
  box = 14,
  strokeWidth = 1.1,
  label,
  tone,
  children,
}: {
  size?: number;
  /**
   * Система координат. Число — квадрат: 14 у команд, 20 у значков разделов.
   * Пара — своя пропорция, как у шеврона.
   */
  box?: number | readonly [number, number];
  strokeWidth?: number;
  label?: string;
  tone?: IconTone;
  children: ReactNode;
}) {
  const [boxW, boxH] = typeof box === 'number' ? [box, box] : box;
  const scale = size / boxH;

  return (
    <svg
      width={boxW * scale}
      height={size}
      viewBox={`0 0 ${boxW} ${boxH}`}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={label === undefined ? true : undefined}
      role={label === undefined ? undefined : 'img'}
      aria-label={label}
      focusable="false"
      className={clsx(styles.icon, tone !== undefined && styles[tone])}
    >
      {children}
    </svg>
  );
}
